import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const LAND_TYPES = ['Farmland', 'Orchard', 'Plantation', 'Greenhouse', 'Other'];
const AREA_UNITS = ['Acre', 'Hectare', 'Bigha', 'Cent'];

const EMPTY_ADD_FORM = {
  listingType: 'lease', title: '', price: '', address: '', area: '',
  areaUnit: 'Acre', landType: 'Farmland', frontage: '', soilType: '',
  landUse: '', description: '', photos: '',
};

const STATUS_COLORS = {
  pending:  { bg:'var(--color-warning-light)', color:'var(--color-warning)', label:'⏳ Pending' },
  active:   { bg:'var(--color-success-light)', color:'var(--color-success)', label:'🟢 Live' },
  rejected: { bg:'var(--gray-100)',            color:'#DC2626',              label:'❌ Rejected' },
};

export default function AdminLandPage() {
  const navigate = useNavigate();
  const [tab, setTab]             = useState('listings');   // listings | category
  const [stats, setStats]         = useState(null);
  const [listings, setListings]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage]           = useState(1);
  const [filters, setFilters]     = useState({ status:'', listingType:'', landType:'', search:'' });
  const [editListing, setEditListing] = useState(null);   // listing being status-edited
  const [newStatus, setNewStatus]     = useState('');
  const [adminNote, setAdminNote]     = useState('');
  const [saving, setSaving]           = useState(false);

  const [showAdd, setShowAdd]     = useState(false);
  const [addForm, setAddForm]     = useState(EMPTY_ADD_FORM);
  const [addSaving, setAddSaving] = useState(false);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { if (tab === 'listings') loadListings(); }, [tab, filters, page]);

  const loadStats = async () => {
    try { const { data } = await api.get('/admin/land/stats'); setStats(data.stats); }
    catch {}
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20, ...Object.fromEntries(Object.entries(filters).filter(([,v])=>v)) });
      const { data } = await api.get(`/admin/land?${params}`);
      setListings(data.listings || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load listings'); }
    finally { setLoading(false); }
  };

  const setFilter = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };

  const openStatusEdit = (listing) => {
    setEditListing(listing); setNewStatus(listing.status); setAdminNote('');
  };

  const saveStatus = async () => {
    if (!editListing) return;
    setSaving(true);
    try {
      await api.put(`/admin/land/${editListing._id}/status`, { status: newStatus, adminNote });
      toast.success(`Listing status → ${newStatus} ✅`);
      setListings(p => p.map(l => l._id === editListing._id ? { ...l, status: newStatus } : l));
      setEditListing(null);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const setAddField = (k, v) => setAddForm(p => ({ ...p, [k]: v }));

  const openAdd = () => { setAddForm(EMPTY_ADD_FORM); setShowAdd(true); };

  const submitAdd = async () => {
    if (!addForm.price)          return toast.error('Price is required');
    if (!addForm.address.trim()) return toast.error('Address is required');

    setAddSaving(true);
    try {
      const photos = addForm.photos.split('\n').map(s => s.trim()).filter(Boolean);
      const { data } = await api.post('/admin/land', { ...addForm, photos });
      toast.success('Listing added and published ✅');
      setShowAdd(false);
      setListings(p => [data.listing, ...p]);
      setTotal(p => p + 1);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add listing');
    } finally {
      setAddSaving(false);
    }
  };

  const deleteListing = async (id, title) => {
    if (!window.confirm(`Delete listing "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/land/${id}`);
      setListings(p => p.filter(l => l._id !== id));
      setTotal(p => p - 1);
      loadStats();
      toast.success('Listing deleted');
    } catch { toast.error('Error deleting listing'); }
  };

  // Export to CSV
  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
      const { data } = await api.get(`/admin/land/export?${params}`);
      const rows = data.data;
      if (!rows.length) return toast.error('No data to export');
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h]??'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type:'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a'); a.href = url; a.download = `zevazeva-land-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} listings`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  // Export to simple HTML then print as PDF
  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
      const { data } = await api.get(`/admin/land/export?${params}`);
      const rows = data.data;
      if (!rows.length) return toast.error('No data');
      const html = `<html><head><title>Land Report</title>
        <style>body{font-family:sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}
        th{background:#166534;color:white;padding:6px 8px;text-align:left}
        td{padding:5px 8px;border-bottom:1px solid #eee}tr:nth-child(even){background:#f9f9f9}
        h2{color:#166534}@media print{button{display:none}}</style></head>
        <body><h2> ZevaZeva — Land Report</h2>
        <p>Generated: ${new Date().toLocaleString('en-IN')} | Total: ${rows.length} listings</p>
        <table><tr>${Object.keys(rows[0]).map(h=>`<th>${h}</th>`).join('')}</tr>
        ${rows.map(r=>`<tr>${Object.values(r).map(v=>`<td>${v??''}</td>`).join('')}</tr>`).join('')}
        </table><script>window.print()</script></body></html>`;
      const w = window.open('','_blank'); w.document.write(html); w.document.close();
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const STAT_CARDS = stats ? [
    { label:'Total Listings', value: stats.total,    icon:'🏞️', color:'var(--primary)' },
    { label:'Pending',        value: stats.pending,  icon:'⏳', color:'var(--color-warning)' },
    { label:'Live',           value: stats.active,   icon:'🟢', color:'var(--color-success)' },
    { label:'Rejected',       value: stats.rejected, icon:'❌', color:'#DC2626' },
    { label:'This Week',      value: stats.newThisWeek, icon:'📅', color:'var(--secondary)' },
  ] : [];

  return (
    <div className="page">
      {/* Header */}
      <div className="header">
        <div style={{ display:'flex', alignItems:'center', gap:'var(--sp-3)', marginBottom:'var(--sp-3)' }}>
          <button className="header__back" onClick={() => navigate('/admin')}>←</button>
          <div style={{ flex:1 }}>
            <div className="header__title">🏞️ Land Management</div>
            <div className="header__subtitle">Verify, approve, and manage property listings</div>
          </div>
          <button className="header__action" onClick={openAdd}>
            ➕ Add Land
          </button>
          <button className="header__action" onClick={exportCSV} disabled={exporting}>
            {exporting ? '...' : ' CSV'}
          </button>
          <button className="header__action" onClick={exportPDF} disabled={exporting}>
            📄 PDF
          </button>
        </div>

        {/* Stat cards in header */}
        {stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'var(--sp-2)' }}>
            {STAT_CARDS.map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,.15)', borderRadius:'var(--r-sm)', padding:'var(--sp-2) var(--sp-3)', textAlign:'center' }}>
                <div style={{ fontSize:18 }}>{s.icon}</div>
                <div style={{ color:'white', fontWeight:700, fontSize:18, lineHeight:1 }}>{s.value}</div>
                <div style={{ color:'rgba(255,255,255,.75)', fontSize:10, marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab${tab==='listings'?' tab--active':''}`} onClick={()=>setTab('listings')}>🏞️ All Listings <span className="tab__count">{total}</span></button>
        <button className={`tab${tab==='category'?' tab--active':''}`} onClick={()=>setTab('category')}> By Land Type</button>
      </div>

      {/* Filters (only on listings tab) */}
      {tab === 'listings' && (
        <div style={{ background:'white', padding:'var(--sp-3) var(--sp-4)', borderBottom:'1px solid var(--gray-100)' }}>
          {/* Status pills */}
          <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
            {['','pending','active','rejected'].map(s => (
              <button key={s} className={`pill${filters.status===s?' pill--active':''}`} onClick={()=>setFilter('status',s)}>
                {s ? STATUS_COLORS[s]?.label : '🌐 All Status'}
              </button>
            ))}
          </div>
          {/* Listing type pills */}
          <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
            <button className={`pill${!filters.listingType?' pill--active':''}`} onClick={()=>setFilter('listingType','')}>All Types</button>
            <button className={`pill${filters.listingType==='lease'?' pill--active':''}`} onClick={()=>setFilter('listingType','lease')}>Lease</button>
            <button className={`pill${filters.listingType==='sale'?' pill--active':''}`} onClick={()=>setFilter('listingType','sale')}>Sale</button>
          </div>
          {/* Land type pills */}
          <div className="pill-bar" style={{ padding:'0 0 var(--sp-2)', background:'transparent' }}>
            <button className={`pill${!filters.landType?' pill--active':''}`} onClick={()=>setFilter('landType','')}>All Land Types</button>
            {LAND_TYPES.map(c => (
              <button key={c} className={`pill${filters.landType===c?' pill--active':''}`} onClick={()=>setFilter('landType',c)}>{c}</button>
            ))}
          </div>
          {/* Search */}
          <div style={{ display:'flex', gap:'var(--sp-2)' }}>
            <input className="input" placeholder="🔍 Search title or address..." value={filters.search}
              onChange={e=>setFilter('search',e.target.value)} style={{ flex:1 }} />
          </div>
        </div>
      )}

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>

        {/* ── LISTINGS TAB ── */}
        {tab === 'listings' && (
          <>
            {loading ? (
              <div className="full-center"><span className="spinner spinner--lg" /></div>
            ) : listings.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state__icon">🏞️</div>
                <div className="empty-state__title">No listings found</div>
              </div>
            ) : (
              <>
                <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-3)' }}>
                  Showing {listings.length} of {total} listings
                </div>
                {listings.map(item => {
                  const st = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
                  return (
                    <div key={item._id} className="card" style={{ marginBottom:'var(--sp-3)', borderLeft:`4px solid ${st.color}` }}>
                      <div className="card__body">
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'var(--sp-2)' }}>
                          <div>
                            <div className="fw-bold text-lg">{item.title}</div>
                            <div className="text-sm text-muted">{item.listingType === 'lease' ? 'Lease' : 'Sale'} · {item.landType || '-'}</div>
                          </div>
                          <span style={{ background:st.bg, color:st.color, borderRadius:'var(--r-full)', padding:'3px 10px', fontSize:12, fontWeight:600, flexShrink:0 }}>{st.label}</span>
                        </div>

                        {/* Owner info */}
                        <div style={{ background:'var(--gray-50)', borderRadius:'var(--r-sm)', padding:'var(--sp-2) var(--sp-3)', marginBottom:'var(--sp-2)' }}>
                          <div className="text-sm fw-semi">👤 {item.userId?.name || 'Unknown'}</div>
                          <div className="text-xs text-muted">📱 {item.userId?.mobile} ·  {item.address}</div>
                        </div>

                        {/* Listing details */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
                          {[
                            { label:'Area',  value: item.area ? `${item.area} ${item.areaUnit}` : '-' },
                            { label:'Photos',value: item.photos?.length || 0 },
                            { label:'Views', value: item.viewCount || 0 },
                          ].map(d => (
                            <div key={d.label} style={{ textAlign:'center', background:'var(--gray-50)', borderRadius:'var(--r-sm)', padding:'var(--sp-2)' }}>
                              <div className="fw-bold text-md" style={{ color:'var(--primary)' }}>{d.value}</div>
                              <div className="text-xs text-muted">{d.label}</div>
                            </div>
                          ))}
                        </div>

                        {item.price && <div className="text-sm" style={{ marginBottom:'var(--sp-2)' }}>💰 ₹{item.price?.toLocaleString?.() || item.price}{item.listingType==='lease' ? ' / Month' : ''}</div>}

                        {/* Verification docs */}
                        {(item.ownerDocUrl || item.selfieUrl) && (
                          <div className="btn-group" style={{ flexWrap:'wrap', marginBottom:'var(--sp-2)' }}>
                            {item.ownerDocUrl && <a href={item.ownerDocUrl} target="_blank" rel="noreferrer" className="btn btn--outline-gray btn--sm">📄 Owner Doc</a>}
                            {item.selfieUrl   && <a href={item.selfieUrl}   target="_blank" rel="noreferrer" className="btn btn--outline-gray btn--sm">🤳 Selfie</a>}
                            {item.extraDocUrl && <a href={item.extraDocUrl} target="_blank" rel="noreferrer" className="btn btn--outline-gray btn--sm">📎 Extra Doc</a>}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="btn-group" style={{ flexWrap:'wrap' }}>
                          <button className="btn btn--primary btn--sm" onClick={() => openStatusEdit(item)}>
                            🔄 Review / Change Status
                          </button>
                          <button className="btn btn--outline-gray btn--sm" onClick={() => navigate(`/land/${item._id}`)}>
                            👁️ View
                          </button>
                          <button className="btn btn--danger btn--sm" onClick={() => deleteListing(item._id, item.title)}>
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {total > 20 && (
                  <div style={{ display:'flex', justifyContent:'center', gap:'var(--sp-2)', margin:'var(--sp-4) 0' }}>
                    <button className="btn btn--outline-gray btn--sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
                    <span className="text-sm text-muted" style={{ alignSelf:'center' }}>Page {page} of {Math.ceil(total/20)}</span>
                    <button className="btn btn--outline-gray btn--sm" disabled={page>=Math.ceil(total/20)} onClick={()=>setPage(p=>p+1)}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── BY LAND TYPE TAB ── */}
        {tab === 'category' && stats && (
          <>
            <div className="fw-semi text-base" style={{ marginBottom:'var(--sp-3)' }}>Listings by Land Type</div>
            {(stats.byLandType || []).map(cat => {
              const pct = stats.total > 0 ? Math.round((cat.count / stats.total) * 100) : 0;
              return (
                <div key={cat._id || 'unknown'} className="card" style={{ marginBottom:'var(--sp-3)', padding:'var(--sp-3) var(--sp-4)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'var(--sp-2)' }}>
                    <span className="fw-semi text-base">{cat._id || 'Unknown'}</span>
                    <span className="fw-bold text-base" style={{ color:'var(--primary)' }}>{cat.count} listings</span>
                  </div>
                  <div className="progress">
                    <div className="progress__bar" style={{ width:`${pct}%` }} />
                  </div>
                  <div className="text-xs text-muted" style={{ marginTop:'var(--sp-1)' }}>{pct}% of total</div>
                </div>
              );
            })}
          </>
        )}

        <div style={{ height:'var(--sp-8)' }} />
      </div>

      {/* ── STATUS EDIT MODAL ── */}
      {editListing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={() => setEditListing(null)}>
          <div style={{ background:'white', borderRadius:'var(--r-lg) var(--r-lg) 0 0', padding:'var(--sp-5)', width:'100%', maxWidth:'var(--max-w)', margin:'0 auto' }}
            onClick={e=>e.stopPropagation()}>

            <div className="fw-semi text-lg" style={{ marginBottom:'var(--sp-1)' }}>🔄 Review Listing</div>
            <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-4)' }}>
              {editListing.title} · Owner: {editListing.userId?.name}
            </div>

            {/* Status options */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-4)' }}>
              {[
                { value:'pending',  label:'⏳ Pending',  desc:'Awaiting verification' },
                { value:'active',   label:'🟢 Approve',  desc:'Make it live' },
                { value:'rejected', label:'❌ Reject',   desc:'Send back to owner' },
              ].map(s => (
                <button key={s.value} onClick={() => setNewStatus(s.value)}
                  style={{ padding:'var(--sp-3)', borderRadius:'var(--r-sm)', border:`2px solid ${newStatus===s.value?'var(--primary)':'var(--gray-200)'}`, background: newStatus===s.value?'var(--primary-light)':'white', textAlign:'left', cursor:'pointer', transition:'all .15s' }}>
                  <div className="fw-semi text-sm">{s.label}</div>
                  <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>{s.desc}</div>
                </button>
              ))}
            </div>

            {/* Admin note */}
            <div style={{ marginBottom:'var(--sp-4)' }}>
              <label className="label">Admin Note (optional)</label>
              <textarea className="input" rows={2} value={adminNote}
                onChange={e=>setAdminNote(e.target.value)}
                placeholder="Reason for rejection or note for the owner" />
            </div>

            <div className="btn-group">
              <button className="btn btn--primary btn--full" onClick={saveStatus} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Status'}
              </button>
              <button className="btn btn--outline-gray btn--full" onClick={() => setEditListing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD LAND MODAL ── */}
      {showAdd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={() => setShowAdd(false)}>
          <div style={{ background:'white', borderRadius:'var(--r-lg) var(--r-lg) 0 0', padding:'var(--sp-5)', width:'100%', maxWidth:'var(--max-w)', margin:'0 auto', maxHeight:'88vh', overflowY:'auto' }}
            onClick={e=>e.stopPropagation()}>

            <div className="fw-semi text-lg" style={{ marginBottom:'var(--sp-1)' }}>➕ Add Land Listing</div>
            <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-4)' }}>
              Published live immediately — no owner verification needed for admin-added listings.
            </div>

            {/* Listing type */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
              {[{ v:'lease', l:'Lease' }, { v:'sale', l:'Sale' }].map(o => (
                <button key={o.v} onClick={() => setAddField('listingType', o.v)}
                  style={{ padding:'var(--sp-3)', borderRadius:'var(--r-sm)', border:`2px solid ${addForm.listingType===o.v?'var(--primary)':'var(--gray-200)'}`, background: addForm.listingType===o.v?'var(--primary-light)':'white', fontWeight:600, cursor:'pointer' }}>
                  {o.l}
                </button>
              ))}
            </div>

            <div style={{ marginBottom:'var(--sp-3)' }}>
              <label className="label">Title (optional)</label>
              <input className="input" value={addForm.title} onChange={e=>setAddField('title', e.target.value)}
                placeholder="Auto-generated from area + land type if left blank" />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
              <div>
                <label className="label">Price (₹) *</label>
                <input className="input" type="number" value={addForm.price} onChange={e=>setAddField('price', e.target.value)}
                  placeholder={addForm.listingType==='lease' ? 'Per month' : 'Total'} />
              </div>
              <div>
                <label className="label">Land Type</label>
                <select className="input" value={addForm.landType} onChange={e=>setAddField('landType', e.target.value)}>
                  {LAND_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:'var(--sp-3)' }}>
              <label className="label">Address *</label>
              <input className="input" value={addForm.address} onChange={e=>setAddField('address', e.target.value)}
                placeholder="Village, taluk, district" />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
              <div>
                <label className="label">Area</label>
                <input className="input" type="number" value={addForm.area} onChange={e=>setAddField('area', e.target.value)} />
              </div>
              <div>
                <label className="label">Area Unit</label>
                <select className="input" value={addForm.areaUnit} onChange={e=>setAddField('areaUnit', e.target.value)}>
                  {AREA_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'var(--sp-2)', marginBottom:'var(--sp-3)' }}>
              <div>
                <label className="label">Frontage</label>
                <input className="input" value={addForm.frontage} onChange={e=>setAddField('frontage', e.target.value)} />
              </div>
              <div>
                <label className="label">Soil Type</label>
                <input className="input" value={addForm.soilType} onChange={e=>setAddField('soilType', e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom:'var(--sp-3)' }}>
              <label className="label">Land Use</label>
              <input className="input" value={addForm.landUse} onChange={e=>setAddField('landUse', e.target.value)} />
            </div>

            <div style={{ marginBottom:'var(--sp-3)' }}>
              <label className="label">Description</label>
              <textarea className="input" rows={3} value={addForm.description} onChange={e=>setAddField('description', e.target.value)} />
            </div>

            <div style={{ marginBottom:'var(--sp-4)' }}>
              <label className="label">Photo URLs (one per line, optional)</label>
              <textarea className="input" rows={3} value={addForm.photos} onChange={e=>setAddField('photos', e.target.value)}
                placeholder={'https://example.com/photo1.jpg\nhttps://example.com/photo2.jpg'} />
            </div>

            <div className="btn-group">
              <button className="btn btn--primary btn--full" onClick={submitAdd} disabled={addSaving}>
                {addSaving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Publish Listing'}
              </button>
              <button className="btn btn--outline-gray btn--full" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
